import { type QueryClient, queryOptions, useQuery } from "@tanstack/react-query";
import { ApiError, api } from "./api";

export type Locale = "en" | "de";

export type SetupStatus = { needsSetup: boolean; instanceName: string; defaultLocale: Locale };

export type Me = {
  id: string;
  name: string;
  email: string;
  locale: Locale;
  isOwner: boolean;
  /** TOTP is set up for this account. */
  twoFactorEnabled?: boolean;
  /** Admins must enrol TOTP (instance policy) before the app lets them do anything else. */
  twoFactorRequired?: boolean;
  kind: string;
  image: string | null;
  /** Public instance settings every signed-in user may read. */
  instance?: { name: string; priorityStyle: "p" | "word"; defaultLocale: Locale };
};

/**
 * Route guards await these two before anything renders. A hung API (dev proxy has no timeout) must
 * not leave a blank page forever: after 8s the request fails as a network error and the guard falls
 * through to the login form or the route error with retry.
 */
const GUARD_TIMEOUT_MS = 8000;
const guardSignal = (signal: AbortSignal) => AbortSignal.any([signal, AbortSignal.timeout(GUARD_TIMEOUT_MS)]);

export const setupStatusQuery = queryOptions({
  queryKey: ["setup", "status"],
  queryFn: ({ signal }) => api.get<SetupStatus>("/api/v1/setup/status", guardSignal(signal)),
  staleTime: 60_000,
});

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: ({ signal }) => api.get<Me>("/api/v1/me", guardSignal(signal)),
  staleTime: 5 * 60_000,
  retry: false,
});

export function useMe() {
  return useQuery(meQuery);
}

/** Returns the signed-in user, or null on 401. Other errors propagate. */
export async function loadMe(qc: QueryClient): Promise<Me | null> {
  try {
    return await qc.fetchQuery(meQuery);
  } catch (e) {
    if (e instanceof ApiError && e.isUnauthorized) {
      qc.removeQueries({ queryKey: meQuery.queryKey });
      return null;
    }
    throw e;
  }
}

/** Setup status, or null when the API is unreachable (pages then render their own error state). */
export async function loadSetupStatus(qc: QueryClient): Promise<SetupStatus | null> {
  try {
    return await qc.ensureQueryData(setupStatusQuery);
  } catch {
    return null;
  }
}
